import { useI18n, type MessageKey } from '@ecom/i18n';
import { type BadgeTone } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { STATUS_TONE_FIGMA } from '../status-tone';
import type { OrderStatus } from '../types';

/**
 * All 14 DEV §7 status values, not only the ones reachable today (entrée
 * 67's own header: "some enabled rows have no reachable endpoint yet —
 * a sequencing decision, not a bug"). `Record<OrderStatus, ...>` — a
 * status this object does not cover fails to COMPILE, so a future sprint
 * that makes SHIPPED/DELIVERED reachable cannot silently ship a blank
 * badge for it.
 */
export const STATUS_TONE: Record<OrderStatus, BadgeTone> = {
  ORDER_CREATED: 'info',
  PAYMENT_PENDING: 'warn',
  PAYMENT_CONFIRMED: 'good',
  PAYMENT_FAILED: 'critical',
  PROCESSING: 'info',
  PACKED: 'accent',
  CANCELLED: 'critical',
  SHIPPED: 'info',
  DELIVERY_FAILED: 'critical',
  DELIVERED: 'good',
  RETURN_REQUESTED: 'warn',
  RETURNED: 'warn',
  REFUNDED: 'neutral',
  PARTIALLY_REFUNDED: 'neutral',
};

/**
 * Reuses the storefront's own `storefront.orderStatus.*` catalogue keys —
 * same 14 `OrderStatus` values, same exact French wording the merchant
 * dashboard already showed, so this never duplicates a second translation
 * of the same status set (Sprint i18n Phase 3).
 */
export const STATUS_LABEL_KEY: Record<OrderStatus, MessageKey> = {
  ORDER_CREATED: 'storefront.orderStatus.ORDER_CREATED',
  PAYMENT_PENDING: 'storefront.orderStatus.PAYMENT_PENDING',
  PAYMENT_CONFIRMED: 'storefront.orderStatus.PAYMENT_CONFIRMED',
  PAYMENT_FAILED: 'storefront.orderStatus.PAYMENT_FAILED',
  PROCESSING: 'storefront.orderStatus.PROCESSING',
  // Not the customer's wording (« En livraison », shared with SHIPPED): the merchant must tell the two steps apart.
  PACKED: 'merchant.orderStatus.PACKED',
  CANCELLED: 'storefront.orderStatus.CANCELLED',
  SHIPPED: 'storefront.orderStatus.SHIPPED',
  DELIVERY_FAILED: 'storefront.orderStatus.DELIVERY_FAILED',
  DELIVERED: 'storefront.orderStatus.DELIVERED',
  RETURN_REQUESTED: 'storefront.orderStatus.RETURN_REQUESTED',
  RETURNED: 'storefront.orderStatus.RETURNED',
  REFUNDED: 'storefront.orderStatus.REFUNDED',
  PARTIALLY_REFUNDED: 'storefront.orderStatus.PARTIALLY_REFUNDED',
};

/**
 * The status as the Figma design's pill (« Souk 2.0 »): a tint of the status's semantic colour with a dot
 * (`../status-tone.ts`). `STATUS_TONE` stays the semantic tone for the other places that colour a status.
 */
export function OrderStatusBadge({ status }: { status: OrderStatus }): ReactElement {
  const { t } = useI18n();
  return (
    <span className="sk-status" data-tone={STATUS_TONE_FIGMA[status]}>
      {t(STATUS_LABEL_KEY[status])}
    </span>
  );
}
